/*
Inbound conversion: canonical OpenAPI ScenarioTables -> internal redux row shape.

The back end persists scenario data tables using the canonical OpenAPI component
schema (Location/Trip/Vehicle/VehicleType/TravelCost/RouteStop), keyed on stable
`*Id` fields, with no denormalized display columns or coordinates. The redux
state keys rows on `UUID` and expects dropdown "display" columns (names) to be
present. This module bridges the two, so the existing load machinery
(initializeDataHelper -> addRowHelper) and the geoJson builder work unchanged.

It is the inbound mirror of store/functions/openapi_conversion_functions.js
(state -> OpenAPI). It is idempotent and safe on already-internal (legacy) data:
rows that already carry `UUID` keep it, and display columns are re-derived from
the reference ids (which is correct by definition).

See .builder/example-scenarios-schema-audit.md for the field-by-field rationale.
*/
import { generateUUID } from "store/functions/geoJsonFunctions.js";
import { STATIC_TABLE_SETTINGS } from "store/reducers/constants/constants.js";

// Canonical entity-id field per table; these become the row's internal `UUID`.
const ID_FIELD_BY_TABLE = {
  locations: "locationId",
  trips: "tripId",
  vehicles: "vehicleId",
  vehicle_types: "vehicleTypeId",
};

/**
 * Ensure every row has a `UUID`, sourced from its canonical entity id when it
 * has one, otherwise its existing UUID, otherwise a freshly generated id
 * (travel_costs / route_stops have no natural entity id).
 */
function assignUuids(data) {
  const out = {};
  for (const [tableName, rows] of Object.entries(data)) {
    if (!Array.isArray(rows)) {
      out[tableName] = rows;
      continue;
    }
    const idField = ID_FIELD_BY_TABLE[tableName];
    out[tableName] = rows.map((row) => {
      const uuid = (idField && row[idField]) || row.UUID || generateUUID();
      return { ...row, UUID: uuid };
    });
  }
  return out;
}

/**
 * Populate dropdown "display" columns (names) from their reference-id columns,
 * driven by each table's `dropdownSource` config. Canonical data carries only the
 * key columns (e.g. `fromLocationId`, `vehicleTypeIds`); the tables render the
 * display columns (`fromLocationName`, `vehicleTypes`, `Starting Location`, ...).
 *
 * For each dropdownSource entry we look up the referenced source row(s) by
 * `keyFieldSource` (= UUID) and copy each display field across. Key mappings
 * (sourceColumn === keyFieldSource) are skipped — those ids are already present.
 */
function resolveDisplayColumns(data) {
  const byUuid = {};
  for (const [tableName, rows] of Object.entries(data)) {
    if (!Array.isArray(rows)) continue;
    byUuid[tableName] = new Map(rows.map((r) => [r.UUID, r]));
  }

  for (const [tableName, rows] of Object.entries(data)) {
    if (!Array.isArray(rows)) continue;
    const dropdownSource = STATIC_TABLE_SETTINGS[tableName] &&
      STATIC_TABLE_SETTINGS[tableName].dropdownSource;
    if (!dropdownSource) continue;

    for (const cfg of Object.values(dropdownSource)) {
      if (!cfg || !Array.isArray(cfg.values)) continue;
      const sourceRows = byUuid[cfg.tableName];
      if (!sourceRows) continue;

      // display field mappings only (skip the key mapping)
      const displayMappings = cfg.values.filter((v) => v.sourceColumn !== cfg.keyFieldSource);
      if (displayMappings.length === 0) continue;

      for (const row of rows) {
        const keyVals = row[cfg.keyFieldTarget];
        if (keyVals == null) continue;
        const ids = cfg.multi ? (Array.isArray(keyVals) ? keyVals : [keyVals]) : [keyVals];

        for (const mapping of displayMappings) {
          const resolved = ids
            .map((id) => {
              const src = sourceRows.get(id);
              return src ? src[mapping.sourceColumn] : undefined;
            })
            .filter((v) => v !== undefined);
          row[mapping.targetColumn] = cfg.multi ? resolved : resolved[0];
        }
      }
    }
  }
  return data;
}

/**
 * Resolve `fromLocationName`/`toLocationName` from `fromLocationId`/`toLocationId`
 * for every table that references locations (trips, travel_costs). The
 * `dropdownSource` config covers trips but not travel_costs, so this fills the
 * gap uniformly. Only sets a name when the referenced location resolves.
 */
function resolveLocationNames(data) {
  const locations = data.locations;
  if (!Array.isArray(locations)) return data;
  const locationsByUuid = new Map(locations.map((l) => [l.UUID, l]));

  const setName = (row, idKey, nameKey) => {
    const loc = locationsByUuid.get(row[idKey]);
    if (loc) row[nameKey] = loc.locationName;
  };

  for (const rows of Object.values(data)) {
    if (!Array.isArray(rows)) continue;
    for (const row of rows) {
      if (row.fromLocationId != null) setName(row, "fromLocationId", "fromLocationName");
      if (row.toLocationId != null) setName(row, "toLocationId", "toLocationName");
    }
  }
  return data;
}

/**
 * Move cartesian coordinates into the columns the app actually stores them in.
 *
 * The API carries plane coordinates as first-class `x`/`y`, with
 * `latitude`/`longitude` null — the Location schema states the two are exact
 * counterparts (`x = longitude`, `y = latitude`) so a scenario can move between
 * the families without reordering. Internally there is no x/y column at all: a
 * cartesian scenario keeps its plane values in longitude/latitude, and the
 * coordinate system relabels those columns "X"/"Y" and swaps in numeric
 * validation (see tableSettingsOverrides.js). On the way out,
 * openapi_conversion_functions nulls them again.
 *
 * Nothing performed the inbound half of that, so a published cartesian example
 * loaded with null coordinates and every row failing "Input should be numeric
 * for column: latitude" — 137 of them on pcb-drill-path.
 *
 * Only fills coordinates that are empty. A row already carrying
 * latitude/longitude is left alone, so this cannot overwrite real geography and
 * stays idempotent on already-internal data.
 */
function adoptCartesianCoordinates(data) {
  const locations = data.locations;
  if (!Array.isArray(locations)) return data;

  for (const row of locations) {
    const hasGeo = Number.isFinite(row.latitude) && Number.isFinite(row.longitude);
    // 0 is a real coordinate here — a depot at the origin is the norm on a board
    // or a warehouse floor — so this tests finiteness, not truthiness.
    const hasPlane = Number.isFinite(row.x) && Number.isFinite(row.y);
    if (hasGeo || !hasPlane) continue;
    row.latitude = row.y;
    row.longitude = row.x;
  }
  return data;
}

/**
 * Convert canonical scenario data tables into internal, load-ready rows.
 * Returns a new data object; does not mutate the input.
 *
 * @param {Object} data - the `data` block from /saved_scenario (canonical tables).
 * @returns {Object} data with `UUID`s assigned and display columns resolved.
 */
export function canonicalTablesToInternal(data) {
  if (!data || typeof data !== "object") return data;
  const withUuids = assignUuids(data);
  resolveDisplayColumns(withUuids);
  return adoptCartesianCoordinates(resolveLocationNames(withUuids));
}
